import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { useGlass } from 'styles/glass';

/**
 * Icon button with the glass hover treatment, and the same
 * explain-why-it-is-disabled behaviour as [GlassButton].
 *
 * Use for dialog close buttons, toolbar actions, row actions — anywhere a
 * bare icon is the whole control. Because there is no label, the `label`
 * prop is required-ish: it becomes both the accessible name and the hover
 * tooltip, which for an icon-only control is the only thing telling the user
 * what it does.
 *
 *   <GlassIconButton label='Close' onClick={onClose}><CloseIcon /></GlassIconButton>
 */
export default function GlassIconButton({
  label = '',
  disabled = false,
  disabledReason = '',
  onClick,
  sx,
  children,
  ...rest
}) {
  const GLASS = useGlass();
  const softDisabled = disabled && Boolean(disabledReason);
  // A disabled control explains itself; an enabled one just names itself.
  const tip = softDisabled ? disabledReason : label;

  const handleClick = (event) => {
    if (softDisabled) {
      event.preventDefault();
      event.stopPropagation();
      toast.warning(disabledReason, { toastId: `disabled:${disabledReason}` });
      return;
    }
    if (onClick) onClick(event);
  };

  const button = (
    <IconButton
      {...rest}
      disabled={disabled && !softDisabled}
      aria-disabled={disabled || undefined}
      aria-label={label || undefined}
      onClick={handleClick}
      sx={{
        transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        ...(softDisabled
          ? {
              cursor: 'not-allowed',
              color: GLASS.buttonDisabledText,
              backgroundColor: GLASS.buttonDisabled,
              boxShadow: GLASS.buttonDisabledRim,
              '&:hover': {
                backgroundColor: GLASS.buttonQuietHover,
                boxShadow: GLASS.buttonDisabledRim,
              },
            }
          : {
              // The lit-glass hover: the pane catches more light, the rim
              // brightens, and it lifts a little off the surface. See
              // `buttonHoverRim` in styles/glass.js for why this is a
              // lighting change rather than a new colour.
              '&:hover': {
                backgroundColor: GLASS.buttonQuietHover,
                boxShadow: `${GLASS.buttonHoverGlow}, ${GLASS.buttonHoverRim}`,
                transform: 'scale(1.06)',
              },
              '&:active': { transform: 'scale(0.98)' },
            }),
        ...sx,
      }}
    >
      {children}
    </IconButton>
  );

  if (!tip) return button;

  return (
    <Tooltip
      title={tip}
      placement='top'
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={softDisabled ? 4000 : 1500}
    >
      {button}
    </Tooltip>
  );
}
